import { useState, useRef, useEffect } from "react";
import { PaperPlaneRight, CircleNotch, Sparkle, X } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { getHorizons } from "@/lib/planiq";
import { toast } from "sonner";
import LockNotice from "@/components/LockNotice";

const GENERIC_SUGGESTIONS = [
  "Add a quick-win action we can ship early",
  "Make the early-phase milestones more aggressive",
  "Tighten the plan to focus on root causes",
  "Add a measurable KPI to the final phase",
];

function shorten(s, n = 42) {
  const t = (s || "").trim();
  return t.length > n ? t.slice(0, n).trim() + "…" : t;
}

// Build 'Try' prompts from THIS plan's real pillars, horizons and constraints.
function buildSuggestions(pillars = [], planType, constraints) {
  const horizons = getHorizons(planType);
  const firstHorizon = horizons[0]?.label;
  const lastHorizon = horizons[horizons.length - 1]?.label;
  const allConstraints = [
    ...(constraints?.operational_hazards || []),
    ...(constraints?.cultural_friction || []),
    ...(constraints?.resource_limits || []),
  ].filter(Boolean);
  const out = [];
  if (pillars[0]?.name) out.push(`Add a quick-win action under ${pillars[0].name}`);
  if (firstHorizon) out.push(`Make the ${firstHorizon} milestones more aggressive`);
  if (pillars[1]?.name) out.push(`Tighten ${pillars[1].name} to focus on root causes`);
  else if (pillars[0]?.name) out.push(`Tighten ${pillars[0].name} to focus on root causes`);
  if (allConstraints[0]) out.push(`Add an action addressing "${shorten(allConstraints[0])}"`);
  else if (lastHorizon) out.push(`Add a measurable KPI to ${lastHorizon}`);
  const seen = new Set();
  const unique = out.filter((s) => (seen.has(s) ? false : seen.add(s)));
  return (unique.length >= 3 ? unique : GENERIC_SUGGESTIONS).slice(0, 4);
}

export default function ChatSidebar({ planId, pillars, planType, constraints, scope, onClearScope, onCardsUpdate, lockReason }) {
  const suggestions = buildSuggestions(pillars, planType, constraints);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    api.getChat(planId).then(setMessages).catch(() => {});
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [planId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg, _id: `tmp-${Date.now()}` }]);
    setSending(true);
    try {
      const { job_id } = await api.chat(planId, msg, scope || null);
      const start = Date.now();
      pollRef.current = setInterval(async () => {
        const res = await api.chatJob(planId, job_id).catch(() => null);
        if (res && res.status === "ready") {
          clearInterval(pollRef.current);
          setMessages((m) => [...m, { role: "assistant", content: res.reply, _id: `a-${Date.now()}` }]);
          if (res.cards) onCardsUpdate(res.cards);
          setSending(false);
        } else if ((res && res.status === "error") || Date.now() - start > 120000) {
          clearInterval(pollRef.current);
          toast.error("The assistant could not process that command.");
          setSending(false);
        }
      }, 2500);
    } catch (e) {
      console.error(e);
      toast.error("The assistant could not process that command.");
      setSending(false);
    }
  };

  return (
    <aside className="w-full md:w-96 shrink-0 border-l border-gray-200 bg-gray-50 flex flex-col h-full no-print">
      <div className="px-5 py-4 border-b border-gray-200 bg-white">
        <h2 className="flex items-center gap-2 font-display font-bold text-lg tracking-tight">
          <Sparkle size={18} weight="fill" /> Atlas
        </h2>
        <p className="text-xs text-gray-500 mt-0.5">Your planning copilot — refine the plan in plain English.</p>
      </div>

      {scope && (
        <div className="px-5 py-2.5 border-b border-gray-200 bg-gray-100 flex items-center justify-between"
          data-testid="chat-scope-banner">
          <span className="text-xs font-mono text-gray-700 truncate">
            scope: <span className="font-semibold">{scope.label}</span>
          </span>
          <button onClick={onClearScope} data-testid="clear-scope-button" className="text-gray-500 hover:text-gray-900">
            <X size={15} />
          </button>
        </div>
      )}

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4" data-testid="chat-messages">
        {messages.length === 0 && !sending && !lockReason && (
          <div className="space-y-2 pt-2">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-gray-400 mb-3">Try</p>
            {suggestions.map((s) => (
              <button key={s} onClick={() => send(s)} data-testid="chat-suggestion"
                className="block w-full text-left text-sm text-gray-700 border border-gray-200 bg-white px-3 py-2.5 hover:border-gray-900 transition-colors">
                {s}
              </button>
            ))}
          </div>
        )}
        {messages.map((m) => (
          <div key={m._id} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
            <div className={`max-w-[90%] px-3 py-2 text-sm leading-relaxed ${
              m.role === "user"
                ? "bg-gray-950 text-white"
                : "bg-transparent text-gray-900 font-mono border border-gray-200 bg-white"
            }`}>
              {m.content}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="flex items-center gap-2 text-sm text-gray-500 font-mono border border-gray-200 bg-white px-3 py-2">
              <CircleNotch size={14} className="animate-spin" /> rebalancing plan…
            </div>
          </div>
        )}
      </div>

      <div className="border-t border-gray-200 p-3 pb-16 bg-white">
        {lockReason ? (
          <LockNotice reason={lockReason} />
        ) : (
          <div className="flex gap-2">
            <textarea
              data-testid="chat-input"
              rows={2}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Ask Atlas to refine the plan…"
              className="flex-1 resize-none border border-gray-300 rounded-none px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
            />
            <button onClick={() => send()} disabled={sending || !input.trim()} data-testid="chat-send-button"
              className="bg-gray-950 text-white px-3 disabled:opacity-40 hover:bg-gray-700 transition-colors self-stretch">
              <PaperPlaneRight size={18} weight="fill" />
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}
